import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useRegion } from "../../context/RegionContext";
import { INDUSTRY_OPTIONS, SUPPORT_TYPES } from "../../data/site";
import { Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "", organisation: "", position: "", email: "", phone: "",
  project_name: "", project_location: "", industry: "",
  support_required: "", timeframe: "", referral_source: "", message: "",
};

export default function ProjectInquiryForm() {
  const { region } = useRegion();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    for (const req of ["name", "organisation", "email", "industry", "support_required"]) {
      if (!form[req]) { toast.error("Please complete all required fields."); return; }
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, region });
      setDone(true);
      toast.success("Thank you. We will be in touch within two business days.");
      setForm(initial);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="inquiry-success" className="bg-white border border-slate-200 p-10 text-center">
        <div className="w-14 h-14 rounded-full bg-brand-orange/10 text-brand-orange flex items-center justify-center mx-auto mb-5">
          <Check className="w-7 h-7" />
        </div>
        <h3 className="font-serif text-2xl text-brand-blue mb-2">Message received.</h3>
        <p className="text-slate-600 max-w-md mx-auto">We will be in touch within two business days. For urgent matters, please phone us directly.</p>
        <button data-testid="inquiry-reset-btn" onClick={() => setDone(false)} className="btn-secondary mt-6">Submit another inquiry</button>
      </div>
    );
  }

  const inputCls = "w-full px-4 py-3 border border-slate-300 bg-white text-slate-900 text-sm focus:outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue rounded-sm";
  const labelCls = "block text-xs uppercase tracking-[0.18em] font-semibold text-slate-600 mb-2";

  return (
    <form data-testid="inquiry-form" onSubmit={submit} className="bg-white border border-slate-200 p-8 lg:p-10 shadow-sm">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Name*</label>
          <input data-testid="inq-name" className={inputCls} value={form.name} onChange={(e) => update("name", e.target.value)} required />
        </div>
        <div>
          <label className={labelCls}>Organisation*</label>
          <input data-testid="inq-org" className={inputCls} value={form.organisation} onChange={(e) => update("organisation", e.target.value)} required />
        </div>
        <div>
          <label className={labelCls}>Position</label>
          <input data-testid="inq-position" className={inputCls} value={form.position} onChange={(e) => update("position", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Email*</label>
          <input data-testid="inq-email" type="email" className={inputCls} value={form.email} onChange={(e) => update("email", e.target.value)} required />
        </div>
        <div>
          <label className={labelCls}>Phone</label>
          <input data-testid="inq-phone" className={inputCls} value={form.phone} onChange={(e) => update("phone", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Referral source</label>
          <input data-testid="inq-referral" className={inputCls} placeholder="How did you hear about us?" value={form.referral_source} onChange={(e) => update("referral_source", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Project name</label>
          <input data-testid="inq-project-name" className={inputCls} value={form.project_name} onChange={(e) => update("project_name", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Location</label>
          <input data-testid="inq-project-location" className={inputCls} placeholder={region === "AU" ? "e.g. Central Queensland" : "e.g. West Texas"} value={form.project_location} onChange={(e) => update("project_location", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Industry*</label>
          <select data-testid="inq-industry" className={inputCls} value={form.industry} onChange={(e) => update("industry", e.target.value)} required>
            <option value="">Select industry</option>
            {INDUSTRY_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Support required*</label>
          <select data-testid="inq-support" className={inputCls} value={form.support_required} onChange={(e) => update("support_required", e.target.value)} required>
            <option value="">What kind of support are you looking for?</option>
            {SUPPORT_TYPES.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Timeframe</label>
          <input data-testid="inq-timeframe" className={inputCls} placeholder="e.g. Immediate / Next quarter / Scoping" value={form.timeframe} onChange={(e) => update("timeframe", e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Tell us more (optional)</label>
          <textarea data-testid="inq-message" rows={5} className={inputCls} value={form.message} onChange={(e) => update("message", e.target.value)} />
        </div>
      </div>
      <div className="mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <p className="text-xs text-slate-500 max-w-md">We treat inquiries as confidential. Submissions are reviewed personally by Andrea and the All Things Social Impact advisory team.</p>
        <button data-testid="inquiry-submit-btn" type="submit" disabled={loading} className="btn-primary min-w-[220px] justify-center">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Submit inquiry"}
        </button>
      </div>
    </form>
  );
}
